import * as R from 'ramda'
import { capTitle, collectToLookup, mapOverValues } from '../../helpers'
import { apiGet, apiPost } from '../api'

const userDisplayName = ({ id, username, firstName, lastName }) => {
  firstName = firstName ? capTitle(firstName.trim()) : ''
  lastName = lastName ? capTitle(lastName.trim()) : ''
  if (firstName || lastName) {
    return [lastName, firstName].filter(Boolean).join(' ')
  }
  username = username ? username.trim() : ''
  return username || `Пользователь ${id}`
}

export default { // user information
  namespaced: true,
  state: {
    current: null,
    userMap: null,
  },
  getters: {
    // list of all users (active or not) sorted by displayName or null
    allUsers: (state, getters) => R.unless(R.equals(null), R.pipe(
      R.toPairs,
      R.map(([userId, user]) => [Number(userId), user]),
      R.sortBy(([userId, _]) => getters.displayName(userId)),
      R.map(R.prop(1)),
    ))(state.userMap),
    // list of active users or null
    list: (state, getters) => R.unless(
      R.equals(null),
      R.filter(R.prop('active')),
    )(getters.allUsers),
    // list of inactive users or null
    inactive: (state, getters) => R.unless(
      R.equals(null),
      R.filter(R.complement(R.prop('active'))),
    )(getters.allUsers),
    // Возвращает пользователей, относящихся к текущему пользователю,
    // у админа - все пользователи, у обычного - он сам и его подчиненные
    relevant: (state, getters) => {
      if (!state.current || !state.userMap) return null
      if (state.current.isAdmin) return getters.list
      return getters.list && getters.list.filter(({ id }) =>
        state.current.id === id || state.current.subordinates.includes(id))
    },
    // Подчиненные текущего пользователя
    subordinates: (state, getters) =>
      state.current && getters.list && getters.list.filter(({ id }) =>
        state.current.subordinates.includes(id)),
    // Руководители текущего пользователя
    chiefs: (state, getters) =>
      state.current && getters.list && getters.list.filter(({ id }) =>
        state.current.chiefs.includes(id)),
    // Все пользователи кроме текущего, его руководителей и подчиненных
    unrelated: (state, getters) =>
      state.current && getters.list && getters.list.filter(({ id }) =>
        id !== state.current.id &&
        !state.current.chiefs.includes(id) &&
        !state.current.subordinates.includes(id)),
    // вспомогательный getter для displayName
    displayNameMap: (state) =>
      mapOverValues(userDisplayName, {
        ...(state.userMap || {}),
        ...(state.current ? { [state.current.id]: state.current } : {}),
      }),
    // функциональный геттер - имя пользователя для отображения
    displayName: (_, getters) =>
      id => getters.displayNameMap[id] || userDisplayName({ id }),
    // функциональный геттер - инициалы пользователя
    initials: (state) => id => {
      const user = state.current && state.current.id === id
        ? state.current
        : state.userMap && state.userMap[id]
      if (user && user.firstName && user.lastName) {
        return ['firstName', 'lastName']
          .map(prop => user[prop][0].toUpperCase())
          .join('')
      }
      return `#${id}`
    },
  },
  mutations: {
    setCurrent: (state, { user }) => { state.current = user },
    setUsers: (state, { users }) => {
      state.userMap = collectToLookup('id', users)
    },
    setSubordinates: (state, { userId, subordinates }) => {
      state.userMap[userId].subordinates = [...subordinates]
    },
    setFollow: (state, { follow }) => {
      state.current.follow = [...follow]
      if (state.userMap && state.userMap[state.current.id]) {
        state.userMap[state.current.id].follow = [...follow]
      }
    },
    setIsWorking: (state, { isWorking }) => {
      state.current.isWorking = !!isWorking
      if (state.userMap && state.userMap[state.current.id]) {
        state.userMap[state.current.id].isWorking = !!isWorking
      }
    },
  },
  actions: {
    // текущий пользователь
    getCurrent: ({ rootState, commit, dispatch }) => {
      if (!rootState.auth.token) {
        commit('setCurrent', { user: null })
        return Promise.resolve()
      }
      return apiGet({ url: 'api/user/current/', rootState, dispatch })
        .then(({ res: user }) => commit('setCurrent', { user }))
    },
    // список пользователей:
    // all=true: все пользователи компании
    // all=false: сам пользователь и его подчиненные
    // у админа компании всегда все пользователи
    getList: ({ rootState, commit, dispatch, state }, { reload = false } = {}) => {
      if (!rootState.auth.token) {
        commit('setList', { users: null })
        return Promise.resolve()
      }
      if (!reload && state.userMap !== null) return Promise.resolve()
      return apiGet({ url: 'api/user/list/?all=true', rootState, dispatch })
        .then(({ res: users }) => commit('setUsers', { users }))
    },
    // Установить подчиненных пользователя
    setSubordinates:
      ({ rootState, commit, dispatch }, { userId, subordinateIds }) => {
        return apiPost({
          rootState,
          dispatch,
          url: 'api/user/set-subordinates/',
          data: { chief: userId, subordinates: subordinateIds },
        })
          .then(({ res: { subordinates } }) =>
            commit('setSubordinates', { userId, subordinates }))
      },
    // Подписаться на посты userId
    follow:
      ({ rootState, commit, dispatch }, { userId }) => {
        return apiPost({
          rootState,
          dispatch,
          url: 'api/user/follow/',
          data: { userId },
        })
          // follow - список id пользователей, на которых в результате подписаны
          .then(({ res: follow }) =>
            commit('setFollow', { follow }))
      },
    // Отписаться от постов userId
    unfollow:
      ({ rootState, commit, dispatch }, { userId }) => {
        return apiPost({
          rootState,
          dispatch,
          url: 'api/user/unfollow/',
          data: { userId },
        })
          // follow - список id пользователей, на которых в результате подписаны
          .then(({ res: follow }) =>
            commit('setFollow', { follow }))
      },
  },
}
